import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Switch } from 'react-router-dom'
import { logEvent } from 'firebase/analytics'
import { ForgotPasswordPage, ResetPasswordPage } from 'pages/Auth'
import {
  CreatePage as championshipCreatePage,
  EditPage as championshipEditPage,
  profilePage as championshipProfilePage
} from 'pages/Championship'
import {
  CreateBibNumberPage as EventsCreateBibNumberPage,
  CreatePage as EventsCreatePage,
  EditBibNumberPage as EventsEditBibNumberPage,
  EditPage as EventsEditPage,
  InscriptionFormPage,
  InscriptionPage,
  InscriptionRegisteredListPage,
  LatestResultEventsPage,
  MyCreatedEventsPage,
  MyEventsPage,
  MyLastResultEventsPage,
  MyUpcomingEventsPage,
  NeedHelpPage as EventsNeedHelpPage,
  ProfilePage as EventsProfilePage,
  SearchPage as EventsSearchPage,
  UpcomingEventsPage
} from 'pages/Events'
import { HomePage } from 'pages/HomePage'
import {
  JobPage,
  OrganizationPage,
  PrivacyPolicyPage,
  TermsAndConditionsPage,
  TimingPage
} from 'pages/Landing'
import {
  CreatePage as OrganizationsCreatePage,
  OnboardingPage as OrganizationsOnboardingPage,
  ProfilePage as OrganizationsProfilePage,
  SearchPage as OrganizationsSearchPage
} from 'pages/Organizations'
import {
  FrequentQuestionsPage as UserAccountsFrequentQuestionsPage,
  ProfilePage as UserAccountsProfilePage,
  SearchPage as UserAccountsSearchPage
} from 'pages/UserAccounts'
import { ROUTES } from 'routes'
import { verifyToken } from 'state/modules/auth'
import { USER_ROLE } from 'utils/constants'
import { analytics } from 'utils/firebase'
import { PrivateRoute } from 'utils/routing'

import 'assets/fonts/hurme-geometric-sans-3/stylesheet.css'

export const App = () => {
  const { user } = useSelector((state) => state.auth)
  const dispatch = useDispatch()

  const tryLogAnalyticsEvent = () => {
    if (!process.env.REACT_APP_FIREBASE_APP_ID) {
      return
    }

    logEvent(analytics, 'page_view', {
      page_title: document.title,
      page_location: window.location.href
    })
  }

  useEffect(() => {
    tryLogAnalyticsEvent()

    if (!user) {
      dispatch(verifyToken())
    }
  }, [dispatch, user])

  return (
    <Switch>
      <Route exact path={ROUTES.ROOT} component={HomePage} />

      <Route exact path={ROUTES.TERMS_AND_CONDITIONS} component={TermsAndConditionsPage} />

      <Route exact path={ROUTES.PRIVACY_POLICY} component={PrivacyPolicyPage} />

      <Route exact path={ROUTES.FORGOT_PASSWORD} component={ForgotPasswordPage} />

      <Route exact path={`${ROUTES.RESET_PASSWORD}/:token/:userId`} component={ResetPasswordPage} />

      <Route exact path={ROUTES.EVENTS.SEARCH} component={EventsSearchPage} />
      <PrivateRoute
        exact
        path={ROUTES.EVENTS.INSCRIPTION_LIST}
        component={InscriptionRegisteredListPage}
      />
      <PrivateRoute
        exact
        path={ROUTES.EVENTS.INSCRIPTION_ASSIGN_NUMBER}
        component={InscriptionRegisteredListPage}
      />
      <PrivateRoute
        exact
        path={ROUTES.EVENTS.CREATE_BIB_NUMBER}
        component={EventsCreateBibNumberPage}
      />

      <PrivateRoute
        exact
        path={ROUTES.EVENTS.EDIT_BIB_NUMBER}
        component={EventsEditBibNumberPage}
      />

      <Route exact path={`${ROUTES.EVENTS.PROFILE}/:slugUrlOrId`} component={EventsProfilePage} />

      <PrivateRoute
        exact
        path={ROUTES.EVENTS.CREATE}
        component={EventsCreatePage}
        authenticatedProps={{
          verifyRole: true,
          role: USER_ROLE.ORGANIZER
        }}
      />
      <PrivateRoute exact path={ROUTES.EVENTS.MY_EVENTS} component={MyEventsPage} />
      <PrivateRoute
        exact
        path={ROUTES.EVENTS.MY_UPCOMING_EVENTS}
        component={MyUpcomingEventsPage}
      />
      <PrivateRoute
        exact
        path={`${ROUTES.EVENTS.INSCRIPTION}/:slugUrlOrId`}
        component={InscriptionPage}
      />
      <Route exact path={ROUTES.EVENTS.INSCRIPTION_FORM} component={InscriptionFormPage} />

      <Route
        exact
        path={`${ROUTES.EVENTS.INSCRIPTION_FORM}/:eventInscriptionId`}
        component={InscriptionFormPage}
      />

      <PrivateRoute
        exact
        path={ROUTES.EVENTS.MY_LATEST_RESULT_EVENTS}
        component={MyLastResultEventsPage}
      />
      <PrivateRoute exact path={ROUTES.EVENTS.MY_CREATED_EVENTS} component={MyCreatedEventsPage} />
      <Route exact path={ROUTES.EVENTS.UPCOMING_EVENTS} component={UpcomingEventsPage} />
      <Route exact path={ROUTES.EVENTS.LATEST_RESULTS_EVENTS} component={LatestResultEventsPage} />
      <Route
        exact
        path={`${ROUTES.EVENTS.PROFILE}/:slugUrlOrId/:idCircuitCategory`}
        component={EventsProfilePage}
      />

      <PrivateRoute exact path={`${ROUTES.EVENTS.EDIT}/:slugUrlOrId`} component={EventsEditPage} />
      <PrivateRoute
        exact
        path={`${ROUTES.EVENTS.NEED_HELP}/:slugUrlOrId`}
        component={EventsNeedHelpPage}
      />

      <Route exact path={ROUTES.ORGANIZATIONS.SEARCH} component={OrganizationsSearchPage} />
      <Route
        exact
        path={`${ROUTES.ORGANIZATIONS.PROFILE}/:handleOrId`}
        component={OrganizationsProfilePage}
      />
      <PrivateRoute
        exact
        path={ROUTES.ORGANIZATIONS.CREATE}
        component={OrganizationsCreatePage}
        authenticatedProps={{
          verifyRole: true,
          role: USER_ROLE.ORGANIZER
        }}
      />
      <PrivateRoute
        exact
        path={`${ROUTES.ORGANIZATIONS.ONBOARDING}/:handleOrId`}
        component={OrganizationsOnboardingPage}
        authenticatedProps={{
          verifyRole: true,
          role: USER_ROLE.ORGANIZER
        }}
      />

      <Route
        exact
        path={ROUTES.USERS_ACCOUNTS.FREQUENT_QUESTIONS}
        component={UserAccountsFrequentQuestionsPage}
      />
      <Route exact path={ROUTES.USERS_ACCOUNTS.SEARCH} component={UserAccountsSearchPage} />
      <Route
        exact
        path={`${ROUTES.USERS_ACCOUNTS.PROFILE}/:usernameOrId`}
        component={UserAccountsProfilePage}
      />

      <Route exact path={ROUTES.LANDING.TIMING} component={TimingPage} />
      <Route exact path={ROUTES.LANDING.JOB} component={JobPage} />
      <Route exact path={ROUTES.LANDING.ORGANIZATION} component={OrganizationPage} />

      <Route
        exact
        path={`${ROUTES.CHAMPIONSHIPS.PROFILE}/:slugUrlOrId`}
        component={championshipProfilePage}
      />

      <PrivateRoute
        exact
        path={`${ROUTES.CHAMPIONSHIPS.EDIT}/:slugUrlOrId`}
        component={championshipEditPage}
        authenticatedProps={{
          verifyRole: true,
          role: USER_ROLE.ORGANIZER
        }}
      />

      <PrivateRoute
        exact
        path={ROUTES.CHAMPIONSHIPS.CREATE}
        authenticatedProps={{
          verifyRole: true,
          role: USER_ROLE.ORGANIZER
        }}
        component={championshipCreatePage}
      />
    </Switch>
  )
}

export default App
